import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, NgForm, Validators} from '@angular/forms';
import {ForwardingNgFormRef, LocalFormGroupValidationErrors} from '../../../../../lib/util/services';
import {MultiselectOptionItem, UiConstants} from '../../../../../util/core-utils';
import {Invoice} from '../../../../../lib/invoice/invoice/invoice.service';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {InvoiceSettings} from '../../../../../lib/invoice/invoice-settings/invoice-settings.service';
import {AppValidators} from '../../../../../util/app-validators';
import {NgbDatePickerParserFormatter} from '../../../../../util/ngb-datepicker';
import {EmptyMessage} from '../../../../../lib/util/messages';
import {InputMask} from '../../../../../util/input-masks';
import {OutgoingSms, TaskRecordOutgoingSmsService} from "../../../../../lib/sms/outgoing/task-record-outgoing-sms.service";
import {PhoneNumbers} from "../../../../../lib/util/phone-number";
import {SmsTemplate} from "../../../../../lib/sms/sms-template/sms-template.service";
import {Angular2Multiselects} from "../../../../../util/multiselect";
import {
  RoleImportDialogData,
  RoleImportDialogResult
} from "../../../../role/role-import-dialog/role-import-dialog.component";

@Component({
  selector: 'app-task-record-outgoing-sms-send-dialog',
  templateUrl: './task-record-outgoing-sms-send-dialog.component.html',
  styleUrls: ['./task-record-outgoing-sms-send-dialog.component.scss']
})
export class TaskRecordOutgoingSmsSendDialogComponent implements OnInit {

  Invoice = Invoice;
  UiConstants = UiConstants;
  InvoiceSettings = InvoiceSettings;
  InputMask = InputMask;

  model: TaskRecordOutgoingSmsCreateModel = new TaskRecordOutgoingSmsCreateModel();

  @ViewChild('f', {static: true})
  form: NgForm;
  formGroup: FormGroup;
  private formGroupValidationErrors: LocalFormGroupValidationErrors;

  singleLocalTranslateDropdownSettings: Angular2Multiselects.Settings;
  integrationTypes: MultiselectOptionItem<SmsTemplate.SmsIntegrationType>[] = SmsTemplate.integrationTypes;

  constructor(public dialogRef: MatDialogRef<TaskRecordOutgoingSmsSendDialogComponent>,
              private fb: FormBuilder,
              private outgoingSmsService: TaskRecordOutgoingSmsService,
              @Inject(MAT_DIALOG_DATA) public data: TaskRecordOutgoingSmsSendDialogData) {
    this.formGroup = this.createFormGroup(this.fb);
    this.formGroupValidationErrors = LocalFormGroupValidationErrors.ofForm(
      this.createForwardingHtmlForm(),
      this.formGroup
    );
  }

  ngOnInit() {
    this.singleLocalTranslateDropdownSettings = new Angular2Multiselects.SettingsBuilder()
      .singleSelection(true)
      .translate(true)
      .enableSearchFilter(false)
      .remoteSearch(false)
      .enableCheckAll(false)
      .build();

    this.loadModel();
  }

  public static openDialog(
    dialog: MatDialog,
    data: TaskRecordOutgoingSmsSendDialogData,
    resultCallback: (result?: TaskRecordOutgoingSmsSendDialogResult) => void) {
    const dialogRef = dialog.open(TaskRecordOutgoingSmsSendDialogComponent, {
      closeOnNavigation: true,
      data: data,
      width: '60%',
      height: 'auto',
      panelClass: 'custom-dialog-container'
    });
    dialogRef.afterClosed().subscribe((result?: TaskRecordOutgoingSmsSendDialogResult) => {
      resultCallback(result);
    });
  }

  closeDialog(b?: boolean) {
    this.dialogRef.close({success: b === undefined ? false : b});
  }

  save() {
    if (this.hasLocalFieldError()) {
      return;
    } else {
      this.outgoingSmsCreate();
    }
  }

  hasFieldError(field: Invoice.ValidatedField): boolean {
    return false;
  }

  removeFieldError(field: Invoice.ValidatedField) {
  }

  getFieldErrorText(field: Invoice.ValidatedField): string {
    return '';
  }

  private createForwardingHtmlForm() {
    return new ForwardingNgFormRef({
      formFn: () => {
        return this.form;
      }
    });
  }

  private createFormGroup(fb: FormBuilder): FormGroup {
    return fb.group(
      {
        recipient: fb.control(
          {value: this.model.recipient},
          [Validators.required, AppValidators.validatePhoneNumber]
        ),
        content: fb.control(
          {value: this.model.content},
          [Validators.required]
        ),
        integrationType: fb.control(
          {value: this.model.integrationType},
          [Validators.required]
        ),
      }
    );
  }

  hasLocalFieldError(formControlName?: string, errorCode?: string): boolean {
    return this.formGroupValidationErrors.hasFieldError(formControlName, errorCode);
  }

  outgoingSmsCreate() {
    this.outgoingSmsService.send({
      taskId: this.data.taskId,
      taskRecordId: this.data.taskRecordId,
      message: this.model.content,
      previousSmsId: this.data.outgoingSms?.id,
      integrationType: this.model.getIntegrationType()!,
      phoneNumber: PhoneNumbers.parse(this.model.recipient),
    }).subscribe(
      (result: EmptyMessage) => {
        this.closeDialog(true);
      }
    );
  }

  private loadModel() {
    this.model = new TaskRecordOutgoingSmsCreateModel();
    if (this.data.outgoingSms) {
      this.model.recipient = this.data.outgoingSms.recipient!.toIso()!;
      this.model.content = this.data.outgoingSms.content!;
      this.model.integrationType = SmsTemplate.integrationTypes
        .filter(i => i.id === this.data.outgoingSms?.integrationType);
    }
  }

  getTitleKey() {
    if (this.data.outgoingSms) {
      return 'TASK_RECORD_OUTGOING_SMS_RESEND';
    }
    return 'TASK_RECORD_OUTGOING_SMS_SEND';
  }
}

class TaskRecordOutgoingSmsCreateModel {
  recipient: string;
  content: string;
  integrationType: MultiselectOptionItem<SmsTemplate.SmsIntegrationType>[];

  getIntegrationType(): SmsTemplate.SmsIntegrationType | undefined {
    if (this.integrationType.length === 0) {
      return undefined;
    }
    return this.integrationType[0].id;
  }
}

export interface TaskRecordOutgoingSmsSendDialogData {
  taskId: number;
  taskRecordId: number;
  outgoingSms?: OutgoingSms.OutgoingSms;
}

export interface TaskRecordOutgoingSmsSendDialogResult {
  success: boolean;
}
